// SI vivier (plan 013) — shared constants & helpers.
//
// All vivier data is keyed on the person's silae matricule (`silae_id`), never
// on a cleaner fiche id. Enum values mirror the backend CHECK constraints
// declared in care_v1.2 (Partie A).

export const EMPLOYEE_STATUS_VIVIER = 'Vivier salarial';
export const EMPLOYEE_STATUS_CURRENT = 'Salarié actuel';

// --- origine ---------------------------------------------------------------
// The care.person_vivier view tags each row with its provenance: a matricule
// built into the vivier salarial, an ex-CDD flagged as re-callable, or a person
// carrying an active warning (non conforme). Mirrors the `origine` column values
// exposed by the backend view — codes are kept verbatim (underscore, no dash) to
// match the backend CHECK constraints; only the FR labels below change.
export const ORIGINE_VIVIER_CONSTRUIT = 'vivier_construit';
export const ORIGINE_EX_CDD_RAPPELABLE = 'ex_cdd_rappelable';
export const ORIGINE_NON_CONFORME = 'non_conforme';

export const ORIGINE_LABELS = {
  [ORIGINE_VIVIER_CONSTRUIT]: 'Nouveau vivier',
  [ORIGINE_EX_CDD_RAPPELABLE]: 'Ancien CDD',
  [ORIGINE_NON_CONFORME]: 'Non conforme',
};

export const ORIGINE_CHOICES = [
  {
    id: ORIGINE_VIVIER_CONSTRUIT,
    name: ORIGINE_LABELS[ORIGINE_VIVIER_CONSTRUIT],
  },
  {
    id: ORIGINE_EX_CDD_RAPPELABLE,
    name: ORIGINE_LABELS[ORIGINE_EX_CDD_RAPPELABLE],
  },
  {
    id: ORIGINE_NON_CONFORME,
    name: ORIGINE_LABELS[ORIGINE_NON_CONFORME],
  },
];

const SILAE_ID_PAD = 5;

// Matricules 99000-99999 are reserved for technical fiches (commercial demo
// 99001, reserved value 99999; plan 114, PO decision of 2026-11-05). They are
// never minted and never count towards the current maximum: on 2026-10-14 the
// demo fiche 99001 became that maximum, and every hire then got 99002, 99003…
const SILAE_ID_RESERVED_FROM = 99000;
const SILAE_ID_MINTABLE_MAX = SILAE_ID_RESERVED_FROM - 1;

// Sentinel matricules that must never be reused / minted (cf. plan key rules).
const SILAE_SENTINELS = new Set(['00881', '99999']);

// --- silae_id normalization ------------------------------------------------
// Must mirror `lpad(btrim(silae_id),5,'0')` on the care side (plan 014): the
// backend writes `cleaner_profile.silae_id` zero-padded to 5 chars, so the UI
// has to key reads/writes on the same normalized matricule — otherwise a legacy
// unpadded value ('881' vs '00881') splits the same person across two profile
// rows.
export const normalizeSilaeId = (raw) => {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  return trimmed === '' ? '' : trimmed.padStart(SILAE_ID_PAD, '0');
};

// --- silae_id minting ------------------------------------------------------
// Single source of truth for "next free matricule". Used by the cleaner create
// dialog and by the dedicated vivier Create form.
export const computeNextSilaeId = async (dataProvider) => {
  const { data } = await dataProvider.getList('cleaner', {
    sort: { field: 'silae_id', order: 'DESC' },
    pagination: { page: 1, perPage: 200 },
    // `silae_id` is a varchar(5): PostgREST sorts and compares it as text.
    // `lte.98999` keeps the reserved fiches out of the 200-row window. On
    // 2026-11-05 every matricule in care has exactly 5 digits, so text order
    // is numeric order. Blind spot: an unpadded value starting with "99"
    // ('990', '9900') would be filtered out although it is below 99000.
    filter: { 'lte(silae_id)': String(SILAE_ID_MINTABLE_MAX) },
  });
  const max = (data || [])
    .map((c) => {
      return c == null ? null : c.silae_id;
    })
    .filter((v) => {
      return v != null && /^\d+$/.test(String(v).trim());
    })
    .map((v) => {
      return parseInt(String(v).trim(), 10);
    })
    // Same rule client-side, in case the filter is not applied upstream.
    .filter((v) => {
      return v < SILAE_ID_RESERVED_FROM;
    })
    .reduce((acc, v) => {
      return v > acc ? v : acc;
    }, 0);
  let next = max + 1;
  // Skip sentinels defensively.
  while (SILAE_SENTINELS.has(String(next).padStart(SILAE_ID_PAD, '0'))) {
    next += 1;
  }
  if (next > SILAE_ID_MINTABLE_MAX) {
    throw new Error(
      `Le prochain silae_id (${next}) dépasse ${SILAE_ID_MINTABLE_MAX} ` +
        `(${SILAE_ID_RESERVED_FROM} à 99999 sont réservés aux fiches techniques). ` +
        `Plage saturée — intervention manuelle requise.`,
    );
  }
  return String(next).padStart(SILAE_ID_PAD, '0');
};

// --- RBAC gate -------------------------------------------------------------
// The backend already 403s the warning resource for non-RH/manager users; this
// is the defensive front-end gate that simply hides the warnings section from a
// cleaner profile. We can only rely on the user object returned by the
// authProvider (localStorage), which carries the cleaner `function`. Office /
// management functions are allowed; a plain field agent is not. Unknown shape
// => hide (fail closed).
const MANAGEMENT_FUNCTIONS = new Set([
  'Manager',
  'Admin',
  'Office Employee',
  'Customer Service',
  'Team Leader',
]);

export const isHrOrManager = (user) => {
  if (!user) {
    return false;
  }
  if (user.is_admin === true || user.isAdmin === true) {
    return true;
  }
  return MANAGEMENT_FUNCTIONS.has(user.function);
};

// --- enum choices (mirror backend CHECK constraints) -----------------------
// `name` stays short (chip / menu label); `description` is the plain-FR gloss
// shown next to the choice so an office user knows what each technique covers.
export const SKILL_CHOICES = [
  {
    id: 'BASIC',
    name: 'Nettoyage classique',
    description: 'Entretien courant : bureaux, sanitaires, sols, poussières.',
  },
  {
    id: 'BIO_CLEANING',
    name: 'Bionettoyage',
    description:
      'Désinfection en milieu sensible (santé, agroalimentaire) — protocoles renforcés.',
  },
  {
    id: 'SCRUBBER_DRIER',
    name: 'Autolaveuse',
    description: 'Conduite d’une autolaveuse pour les grandes surfaces de sol.',
  },
  {
    id: 'STEAM_DUPLEX',
    name: 'Nettoyage vapeur (Duplex)',
    description: 'Nettoyage / désinfection à la vapeur avec machine Duplex.',
  },
];

export const DRIVING_LICENSE_CHOICES = [
  { id: 'NONE', name: 'Aucun' },
  { id: 'B', name: 'Permis B (véhicule léger)' },
];

export const FRENCH_LEVEL_CHOICES = [
  { id: 'EXCELLENT', name: 'Excellent — autonome à l’écrit comme à l’oral' },
  { id: 'CORRECT', name: 'Correct — se débrouille au quotidien' },
  { id: 'LIMITED', name: 'Limité — comprend les consignes simples' },
  { id: 'MINIMAL', name: 'Minimal — quelques mots' },
];

// ⚠️ CONFORMITÉ CNIL / RGPD art. 9 : `LANGUAGE_BARRIER` est un motif de
// COMPÉTENCE PROFESSIONNELLE (compréhension des consignes de travail), JAMAIS
// une donnée d'origine ou de nationalité (donnée sensible interdite). Le libellé
// et l'encart d'avertissement du dialog (WarningsCard) doivent le refléter.
export const WARNING_TYPE_CHOICES = [
  { id: 'IDENTITY_THEFT', name: "Usurpation d'identité" },
  { id: 'FORGED_IDENTITY', name: 'Faux papiers' },
  { id: 'MISCONDUCT_DISMISSAL', name: 'Licenciement pour faute' },
  { id: 'SITE_INCIDENT', name: 'Incident sur site' },
  {
    id: 'LANGUAGE_BARRIER',
    name: 'Barrière linguistique — consignes non comprises',
  },
];

// --- document completeness (B3) --------------------------------------------
// Read-only checklist derived from cleaner_document. A slot is "complete" when
// at least one APPROVED document matching it exists for the silae_id.
//
// Mapping follows THIS repo's type/subtype catalogue (see
// resources/Document/detail/Datagrid.js choices and
// saga/uploadCleanerDocument.js#getTypeFromSubType), not a `health_care`
// document type (which exists but means health insurance/mutuelle, never the
// carte vitale):
//   - Pièce d'identité : type `identity`, with a real ID-card subtype
//     (id_card_*, passport, visa_*, etc.) — EXCLUDING the vitale subtypes.
//   - Carte vitale     : type `identity`, subtype `vitale_card` /
//     `vitale_attestation` (both live under type='identity').
//   - Domicile         : type `home`   (subtype home_receipt & co).
//   - RIB              : type `bank`   (subtype rib).
//
// Identité and vitale share type='identity', so those two slots MUST be
// subtype-sensitive; the others can match on `type` alone.

// Subtypes that, although stored under type='identity', represent the carte
// vitale rather than an identity document.
export const VITALE_SUBTYPES = ['vitale_card', 'vitale_attestation'];

const isIdentityType = (doc) => {
  return !!doc && doc.type === 'identity';
};

const isVitaleSubtype = (doc) => {
  return !!doc && VITALE_SUBTYPES.includes(doc.subtype);
};

export const DOCUMENT_COMPLETENESS_SLOTS = [
  {
    key: 'identity',
    label: "Pièce d'identité",
    // A real identity doc: type='identity' but NOT a vitale subtype.
    match: (doc) => {
      return isIdentityType(doc) && !isVitaleSubtype(doc);
    },
  },
  {
    key: 'home',
    label: 'Justificatif de domicile',
    match: (doc) => {
      return !!doc && doc.type === 'home';
    },
  },
  {
    key: 'vitale',
    label: 'Carte vitale',
    // Vitale lives under type='identity', identified by its subtype.
    match: (doc) => {
      return isIdentityType(doc) && isVitaleSubtype(doc);
    },
  },
  {
    key: 'rib',
    label: 'RIB',
    match: (doc) => {
      return !!doc && doc.type === 'bank';
    },
  },
];

export const DOCUMENT_APPROVED_STATUS = 'APPROVED';

// `documents` = array of cleaner_document rows already fetched for the silae_id.
export const computeDocumentCompleteness = (documents = []) => {
  const approved = documents.filter((d) => {
    return d && d.status === DOCUMENT_APPROVED_STATUS;
  });
  return DOCUMENT_COMPLETENESS_SLOTS.map((slot) => {
    const present = approved.some((d) => {
      return slot.match(d);
    });
    // Don't leak the predicate into the returned slot shape (the card only
    // reads key/label/present).
    return { key: slot.key, label: slot.label, present };
  });
};
